import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { CountryCode, Currency, LanguageCode } from 'src/app/_models';
import { Utilities } from 'src/app/_models/class/utilities';
import { CacheService } from '../cache/cache.service';
import { OrganizationEnum } from 'src/app/_models/enum/organization-enum';

@Injectable({
  providedIn: 'root',
})
export class RegionService {
  private defaultOrganization: OrganizationEnum = OrganizationEnum.VIKINGNO;

  private clientLanguage: LanguageCode = LanguageCode.Norwegian;
  private clientRegion: CountryCode;
  private clientOrganization: OrganizationEnum;
  private isInitialized: boolean = false;

  constructor(
    private translateService: TranslocoService,
    private cache: CacheService,
  ) {
    // Set region from URL by default
    const url: string = window.location.href;
    this.clientRegion = Utilities.getCountryCodeFromUrl(url);
    this.clientOrganization = this.defaultOrganization;
  }

  async init(): Promise<void> {
    // Prevent multiple initializations - only initialize once
    if (this.isInitialized) {
      return;
    }

    await this.setClientRegion();

    // Check for lang query parameter from URL before setting default language
    const urlParams = new URLSearchParams(window.location.search);
    const languageCode = urlParams.get('lang')?.toLowerCase() ?? '';

    if (languageCode && Object.values(LanguageCode).includes(languageCode as LanguageCode)) {
      const lang = languageCode as LanguageCode;
      await this.setClientLanguage(lang);
      localStorage.setItem('language', languageCode);

      const regionFromLang = this.getCountryCodeFromLanguage(lang);
      await this.setClientRegion(regionFromLang);
      const organizationFromLang = this.getOrganizationFromRegion(regionFromLang);
      await this.setClientOrganization(organizationFromLang);
      localStorage.setItem('organizationId', organizationFromLang);
    } else {
      // No valid language in URL, check localStorage first
      const storedLanguage = localStorage.getItem('language');
      if (storedLanguage && Object.values(LanguageCode).includes(storedLanguage as LanguageCode)) {
        // Use stored language preference
        await this.setClientLanguage(storedLanguage as LanguageCode);
      } else {
        // No stored preference, use default logic based on region
        await this.setClientLanguage();
        // Save the default language to localStorage
        localStorage.setItem('language', this.clientLanguage);
      }

      await this.setClientOrganization();
    }

    this.translateService.setDefaultLang(this.clientLanguage);
    this.translateService.setActiveLang(this.clientLanguage);
    // await this.getBrowserLanguage();
    await this.cache.setActiveCountryCode(this.clientRegion);

    this.isInitialized = true;
  }

  // async setClientRegion(region?: CountryCode): Promise<void> {
  //   const storedCountryCode = localStorage.getItem('countryCode');
  //   if (storedCountryCode) {
  //     this.clientRegion = storedCountryCode as CountryCode;
  //     return;
  //   } else {
  //     if (!region) {
  //       const url: string = window.location.href;
  //       this.clientRegion = Utilities.getCountryCodeFromUrl(url);
  //     } else {
  //       this.clientRegion = region;
  //     }
  //   }
  // }

  async setClientRegion(region?: CountryCode): Promise<void> {
    const nextRegion = region ?? Utilities.getCountryCodeFromUrl(window.location.href);
    if (this.clientRegion === nextRegion) {
      return;
    }
    this.clientRegion = nextRegion;
  }

  // async getBrowserLanguage() {
  //   const browserLanguage = navigator.language;
  //   const languageCode = browserLanguage.split('-')[0].toLowerCase() as LanguageCode;
  //   switch (languageCode) {
  //     case LanguageCode.Norwegian: {
  //       this.translateService.setActiveLang(LanguageCode.Norwegian);

  //       const storedLanguage = localStorage.getItem('language');

  //       if (storedLanguage) {
  //         break;
  //       } else {
  //         localStorage.setItem('language', LanguageCode.Norwegian);
  //       }

  //       break;
  //     }
  //     case LanguageCode.Swedish: {
  //       this.translateService.setActiveLang(LanguageCode.Swedish);

  //       const storedLanguage = localStorage.getItem('language');

  //       if (storedLanguage) {
  //         break;
  //       } else {
  //         localStorage.setItem('language', LanguageCode.Swedish);
  //       }
  //       break;
  //     }
  //     case LanguageCode.Danish: {
  //       this.translateService.setActiveLang(LanguageCode.Danish);

  //       const storedLanguage = localStorage.getItem('language');

  //       if (storedLanguage) {
  //         break;
  //       } else {
  //         localStorage.setItem('language', LanguageCode.Danish);
  //       }

  //       break;
  //     }
  //     // case LanguageCode.Finnish: {
  //     //   this.translateService.setActiveLang(LanguageCode.Norwegian);

  //     //   const storedLanguage = localStorage.getItem('language');

  //     //   if (storedLanguage) {
  //     //     break;
  //     //   } else {
  //     //     localStorage.setItem('language', LanguageCode.NO);
  //     //   }

  //     //   break;
  //     // }
  //     // case LanguageCode.English: {
  //     //   this.translateService.setActiveLang(LanguageCode.Norwegian);

  //     //   const storedLanguage = localStorage.getItem('language');

  //     //   if (storedLanguage) {
  //     //     break;
  //     //   } else {
  //     //     localStorage.setItem('language', LanguageCode.NO);
  //     //   }

  //     //   break;
  //     // }
  //     default: {
  //       this.translateService.setActiveLang(LanguageCode.Norwegian);

  //       const storedLanguage = localStorage.getItem('language');

  //       if (storedLanguage) {
  //         break;
  //       } else {
  //         localStorage.setItem('language', LanguageCode.Norwegian);
  //       }

  //       break;
  //     }
  //   }
  // }

  async setClientLanguage(language?: LanguageCode): Promise<void> {
    const nextLanguage = language ?? this.getLanguageFromCountryCode(this.clientRegion);
    if (this.clientLanguage === nextLanguage) {
      return;
    }
    console.log('Setting client language...');
    this.clientLanguage = nextLanguage;
  }

  async setClientOrganization(organization?: OrganizationEnum): Promise<void> {
    const newOrganization =
      organization ??
      (localStorage.getItem('organizationId') as OrganizationEnum) ??
      this.getOrganizationFromRegion(this.clientRegion);
    if (this.clientOrganization === newOrganization) {
      return;
    }
    console.log('Setting client organization...');
    this.clientOrganization = newOrganization;
  }

  private getOrganizationFromRegion(region: CountryCode): OrganizationEnum {
    switch (region) {
      case CountryCode.SE:
        return OrganizationEnum.VIKINGSE;
      case CountryCode.DK:
        return OrganizationEnum.VIKINGDK;
      case CountryCode.NO:
        return OrganizationEnum.VIKINGNO;
      default:
        return OrganizationEnum.VIKINGNO;
    }
  }

  private getCountryCodeFromLanguage(language: LanguageCode): CountryCode {
    switch (language) {
      case LanguageCode.Swedish:
        return CountryCode.SE;
      case LanguageCode.Danish:
        return CountryCode.DK;
      case LanguageCode.Norwegian:
      default:
        return CountryCode.NO;
    }
  }

  getClientRegion(): CountryCode {
    return this.clientRegion;
  }

  getClientLanguage(): LanguageCode {
    return this.clientLanguage;
  }

  getClientOrganization(): OrganizationEnum {
    return this.clientOrganization;
  }

  getClientCurrency(): Currency {
    switch (this.clientRegion) {
      case CountryCode.SE:
        return Currency.SEK;
      case CountryCode.FI:
        return Currency.EUR;
      case CountryCode.DK:
        return Currency.DKK;
      case CountryCode.NO:
        return Currency.NOK;
      default:
        return Currency.NOK;
    }
  }

  getDescriptionForRegion(): string {
    switch (this.clientRegion) {
      case CountryCode.SE:
        return 'My Pages for Viking. Viking erbjuder vägassistans och support dygnet runt i Sverige och Europa. Alltid utan självrisk, oavsett antal assistanser.';
      case CountryCode.FI:
        return 'My Pages for Viking. Viking tarjoaa tiepalvelua ja tukea ympäri vuorokauden Suomessa ja Euroopassa. Aina ilman omavastuuta, tukien lukumäärästä riippumatta.';
      case CountryCode.DK:
        return 'My Pages for Viking. Viking tilbyder vejhjælp og support døgnet rundt i Danmark og Europa. Altid uden selvrisiko, uanset antallet af assistancer.';
      case CountryCode.NO:
        return 'My Pages for Viking. Viking tilbyr veihjelp og støtte hele døgnet i Norge og Europa. Alltid uten egenandel, uavhengig av antall assistanser.';
      default:
        return 'My Pages for Viking. Viking offers roadside assistance and support around the clock in Norway and Europe. Always without a deductible, regardless of the number of assistances.';
    }
  }

  private getLanguageFromCountryCode(countryCode: CountryCode): LanguageCode {
    switch (countryCode) {
      case CountryCode.SE:
        return LanguageCode.Swedish;
      case CountryCode.DK:
        return LanguageCode.Danish;
      case CountryCode.NO:
        return LanguageCode.Norwegian;
      default:
        return LanguageCode.Norwegian; // Default fallback
    }
  }

  /**
   * Sets the client language based on the country code.
   * This should be called when an organization is selected to ensure
   * the language matches the organization's country.
   */
  async setClientLanguageFromCountryCode(countryCode: CountryCode): Promise<void> {
    const language = this.getLanguageFromCountryCode(countryCode);
    await this.setClientLanguage(language);
    this.translateService.setActiveLang(language);
    localStorage.setItem('language', language);
  }
}
